/**
 * Component that sets the search/filter type along with handling search functionality
 */
import React, { useContext, useRef } from "react";
/**
 * Select dropdown documentation see @url https://react-select.com/
 */
import Select, { components } from "react-select";
import styled from "styled-components";
import { IoCloseCircleOutline, IoSearch } from "react-icons/io5";
import { PeriodicTableContext } from "./PeriodicTableContext";

const Search = () => {
  const {
    selectedElements,
    filterType,
    updateSelectedElements,
    updateFilterType,
    updateFormulaSearchTerm,
    dispatch,
  } = useContext(PeriodicTableContext);

  const formulaSearchInput = useRef(null);

  /**
   * Update the current filter selection (elements, formula, or keyword)
   */
  const handleFilterChange = (item) => {
    updateFilterType(item.value);

    dispatch({
      isSearchPerformed: false,
    });
  };

  /**
   * Clear all currently selected elements
   */
  const handleClear = () => {
    updateSelectedElements([]);

    dispatch({
      isSearchPerformed: false,
    });
  };

  /**
   * Handle searching via formula term entered.
   */
  const handleFormulaSubmit = (event) => {
    event.preventDefault();
    updateFilterType("formula");
    updateFormulaSearchTerm(formulaSearchInput.current.value);
    updateSelectedElements([]);
  };

  const searchOptions = [
    { value: "elements", label: "Search by Element" },
    { value: "formula", label: "Search by Formula" }
  ];

  /**
   * Adds the search icon to select dropdown
   * https://react-select.com/components
   */
  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <IoSearch style={{ marginRight: "8px" }} aria-hidden="true" size={20} />
        {children}
      </div>
    </components.SingleValue>
  );

  return (
    <>
      <SearchContainer>
        <Select
          options={searchOptions}
          defaultValue={{
            value: searchOptions[0].value,
            label: searchOptions[0].label,
          }}
          onChange={handleFilterChange}
          className="search-select-container"
          classNamePrefix="search-select"
          components={{ SingleValue }}
          isSearchable={false}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
            colors: {
              ...theme.colors,
              primary: "#2598ca",
            },
          })}
        />
        {filterType === "elements" && !selectedElements.length && (
          <SelectedElements>Select element below</SelectedElements>
        )}

        {filterType === "elements" && selectedElements.length > 0 && (
          <SelectedElements>
            <div>Selected elements:</div>
            <SelectedElements
              style={{ fontWeight: "700", fontSize: "18px", marginLeft: "4px" }}
            >
              {selectedElements.join(" + ")}
            </SelectedElements>
            <Clear onClick={handleClear}>
              <IoCloseCircleOutline aria-hidden="true" size={20} />
              Clear selections
            </Clear>
          </SelectedElements>
        )}

        {filterType === "formula" && (
          <FormContainer>
            <form onSubmit={handleFormulaSubmit}>
              <input
                type="search"
                placeholder="Enter formula, e.g. MoS2"
                aria-label="Enter formula, e.g. MoS2"
                ref={formulaSearchInput}
              />
              <button type="submit">Search</button>
            </form>
          </FormContainer>
        )}

      </SearchContainer>
    </>
  );
};

export default Search;

// Styled components
const SearchContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 8px;
    max-width: 100vw;
  }

  .search-select__single-value {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 1.5px;
  }

  .search-select__option {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 1.5px;
  }
`;

const FormContainer = styled.div`
  display: grid;

  form {
    display: flex;
  }

  input {
    width: 100%;
    padding: 0 16px;
    border: 1px solid lightgray;
    border-radius: 0;
  }

  button {
    padding: 0 16px;
  }
`;

const SelectedElements = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: start;
    margin: 0;
  }
`;

const Clear = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.7;

  &:hover,
  &:focus {
    opacity: 1;
  }

  svg {
    margin-right: 4px;
  }

  @media (max-width: 768px) {
    margin-top: 4px;
    margin-left: 0;
  }
`;
